import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../utils/hooks/useAxios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { produce } from "immer";
import { filterObject } from "../../utils/helpers";

// ******* Queries *******

/**
 * Get pilgrims for a specific mission (arrival)
 * @param {Object} params - Query parameters
 * @param {string} params.mission_id - Mission/serial ID from arrival
 * @param {number} params.page - Current page number
 * @param {number} params.limit - Items per page (default 10)
 * @param {string} params.search - Search query
 * @param {Object<string, string | number>} params.filter - Dynamic filter object with key-value pairs
 * @param {boolean} params.enabled - Whether query should run
 */
export const useGetPilgrimsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { id, type } = useParams();
  const { enabled, ...restParams } = { ...params };
  const query = filterObject(restParams);


  return useQuery({
    queryKey: ["pilgrims", type, id, query],
    queryFn: () => axiosProtected.get("makkah/pilgrims", { params: { ...query, id } }),
    enabled,
    staleTime: 30000, // 30 seconds
  });
};

// ******* Mutations *******

/**
 * Accommodate pilgrims (assign them to housing)
 * Mutation: POST makkah/accommodations
 */
export const useAccommodatePilgrimsMutation = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { type, id } = useParams();

  return useMutation({
    mutationFn: async (payload) => {
      /**
       * Payload structure:
       * {
       *   type: "campaign",
       *   id: 1,
       *   cabinet_number: "cab-2025-001",
       *   pilgrims: [
       *     {
       *       pilgrim_uid: "haj123456",
       *       passport_number: "p1234567",
       *       full_name_ar: "الاسم الكامل",
       *       full_name_en: "full name",
       *       contract_id: 987
       *     }
       *   ]
       * }
       */
      return axiosProtected.post("makkah/accommodations", {
        ...payload,
      });
    },
    onSuccess: (data, variables) => {
      toast.success(t("ACCOMMODATED_SUCCESSFULLY"));
      // Invalidate pilgrims query to refetch with updated data
      // Use the mission_id from the context to invalidate the correct query
      queryClient.invalidateQueries(["one-makkah-group-segment"]);
      queryClient.invalidateQueries(["one-arrival-segment"]);
      queryClient.setQueryData(
        ["pilgrims", type, id, filterObject(params)],
        (old) => {
          return produce(old, (draft) => {
            const pilgrims = draft?.data?.data;

            if (!pilgrims) return draft;

            draft.data.data = pilgrims.map((pilgrim) => ({
              ...pilgrim,
              is_accommodated:
                variables.pilgrims.some((p) => p.id === pilgrim.id) ||
                pilgrim.is_accommodated,
            }));
          });
        }


      );

      queryClient.invalidateQueries({
        queryKey: ["one-arrival-segment", id],
      });
    },
    onError: (err) => {
      console.error("Accommodation error:", err);
      const errorMessage =
        err?.response?.data?.message || t("ACCOMMODATION_FAILED");
      toast.error(errorMessage);
    },
  });
};

/**
 * Cancel accommodation for pilgrims
 * Mutation: POST makkah/accommodations/cancel
 */
export const useCancelAccommodationMutation = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { type, id } = useParams();

  return useMutation({
    mutationFn: async (payload) => {
      /**
       * Payload structure:
       * {
       *   type: "campaign",  // from arrival.type
       *   id: 1,             // from arrival.id
       *   pilgrims: [{ pilgrim_uid: "HAJ123456" }],
       *   mission_id: "434714"  // optional, used for query invalidation only
       * }
       */
      // Extract only API-required fields for the request
      const { type, id, pilgrims } = payload;
      return axiosProtected.post("makkah/accommodations/cancel", {
        type,
        id,
        pilgrims,
      });
    },
    onSuccess: (data, variables) => {
      toast.success(t("ACCOMMODATION_CANCELLED_SUCCESSFULLY"));

      // Invalidate pilgrims query to refetch with updated data
      // Use mission_id from payload if available for invalidation
      queryClient.invalidateQueries(["pilgrims"]);

      queryClient.setQueryData(
        ["pilgrims", type, id, filterObject(params)],
        (old) => {
          return produce(old, (draft) => {
            const pilgrims = draft?.data?.data;

            if (!pilgrims) return draft;

            draft.data.data = pilgrims.map((pilgrim) => ({
              ...pilgrim,
              is_accommodated: variables.pilgrims.some(
                (p) => p.id === pilgrim.id
              )
                ? false
                : pilgrim.is_accommodated,
            }));
          });
        }
      );
    },
    onError: (err) => {
      console.error("Cancel accommodation error:", err);
      const errorMessage =
        err?.response?.data?.message || t("ACCOMMODATION_CANCEL_FAILED");
      toast.error(errorMessage);
    },
  });
};
